import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { TabIdService } from './tab-id.service';

@Injectable({
  providedIn: 'root',
})
export class DirtyStateService {
  private readonly tabIds = inject(TabIdService);
  private dirtyTabsSignal = signal<Map<string, boolean>>(new Map());

  /**
   * Clave con que se guarda y se consulta cada pestaña.
   *
   * <p>Todo entra por {@link TabIdService#claveDeRuta}: la barra pregunta con la ruta, el DOM con
   * `tab-` delante y el formulario con su clave en guiones, y los tres llegan a la MISMA clave.
   * Por eso aqui se compara exacto: antes se comparaba por pedazos y por el ultimo segmento de la
   * ruta, y una sola pestaña sucia hacia que todas las que terminaban en `list` pidieran
   * confirmacion al cerrarse.</p>
   */
  private clave(tabId: string | undefined | null): string {
    return this.tabIds.claveDeRuta(tabId);
  }

  /**
   * Identificadores de las pestañas con cambios sin guardar.
   *
   * <p>Es una SEÑAL, de modo que quien la lea se rehace cuando una pestaña se ensucia o se
   * limpia. `isDirtySync` responde por una sola y no avisa de los cambios: sirve para preguntar,
   * no para reaccionar.</p>
   */
  readonly dirtyTabIds = computed(() =>
    [...this.dirtyTabsSignal().entries()].filter(([, sucio]) => sucio).map(([id]) => id),
  );

  isDirty = computed(() => {
    const map = this.dirtyTabsSignal();
    // Se lee para declarar la dependencia: sin tocar el mapa, el calculo no se rehace cuando
    // una pestaña cambia de estado.
    void map;
    return (tabId: string) => this.isDirtySync(tabId);
  });

  constructor() {
    this.loadFromStorage();

    effect(() => {
      const map = this.dirtyTabsSignal();
      this.saveToStorage(map);
    });
  }

  setDirty(tabId: string, isDirty: boolean): void {
    const normalizedId = this.clave(tabId);
    if (!normalizedId) return;

    this.dirtyTabsSignal.update((currentMap) => {
      const newMap = new Map(currentMap);
      if (isDirty) {
        newMap.set(normalizedId, true);
      } else {
        newMap.delete(normalizedId);
      }
      return newMap;
    });
  }

  /**
   * Si la pestaña tiene cambios sin guardar. Compara EXACTO, por la clave de su pantalla.
   *
   * @param tabId ruta, identificador de pestaña o clave de la pantalla.
   * @returns si tiene cambios sin guardar.
   */
  isDirtySync(tabId: string): boolean {
    const normalizedId = this.clave(tabId);
    if (!normalizedId) return false;
    return this.dirtyTabsSignal().get(normalizedId) ?? false;
  }

  /**
   * Quita la marca de cambios sin guardar de la pestaña. Exacto, como la consulta.
   *
   * @param tabId ruta, identificador de pestaña o clave de la pantalla.
   */
  clear(tabId: string): void {
    const normalizedId = this.clave(tabId);
    if (!normalizedId || !this.dirtyTabsSignal().has(normalizedId)) return;
    this.dirtyTabsSignal.update((currentMap) => {
      const newMap = new Map(currentMap);
      newMap.delete(normalizedId);
      return newMap;
    });
  }

  clearAll(): void {
    this.dirtyTabsSignal.set(new Map());
  }

  getDirtyTabs(): string[] {
    return Array.from(this.dirtyTabsSignal().keys());
  }

  /**
   *  Obtiene el mapa completo de dirty state
   */
  getDirtyMap(): Map<string, boolean> {
    return this.dirtyTabsSignal();
  }

  private saveToStorage(map: Map<string, boolean>): void {
    const data = Array.from(map.entries());
    localStorage.setItem('dirty_tabs', JSON.stringify(data));
  }

  private loadFromStorage(): void {
    const stored = localStorage.getItem('dirty_tabs');
    if (stored) {
      try {
        const data = JSON.parse(stored) as [string, boolean][];
        const map = new Map(data);
        this.dirtyTabsSignal.set(map);
      } catch (e) {
        console.warn('[DirtyStateService] Error al cargar del storage:', e);
      }
    }
  }
}
